import {
	addClass,
	getEl,
	removeAttr,
	removeClass,
	setAttr,
	setText,
} from '../helpers/helpers.js'

const messageSelectors = {
	wrapper: '[data-message]',
	text: '[data-message] p',
}

const spinnerConfig = {
	filename: 'spinner.svg',
	className: 'is-spinning',
}

const messageStatusClasses = {
	success: 'message--success',
	error: 'message--error',
	loading: 'message--loading',
}

const messageTiming = {
	visibleMs: 3200,
	hideMs: 400,
}

export function createFormState(form) {
	const emailInput = getEl('input[type="email"]', form)
	const gdprCheckbox = getEl('[data-gdpr-check]', form)
	const messageWrapper = getEl(messageSelectors.wrapper)
	const messageText = getEl(messageSelectors.text)
	const submitButton = getEl('button[type="submit"], button', form)
	const submitImage = submitButton ? submitButton.querySelector('img') : null
	const formState = {
		form,
		emailInput,
		gdprCheckbox,
		submitButton,
		submitImage,
		submitImageSrc: submitImage?.getAttribute('src') || '',
		messageWrapper,
		messageText,
		messageTimers: {
			hide: null,
			clear: null,
		},
		submitTimers: {
			min: null,
		},
		isSubmitting: false,
		submitStartedAt: 0,
		spinnerLoadHandler: null,
	}
	return formState
}

export function registerFormSubmit(formState) {
	const handler = handleFormSubmit.bind(null, formState)
	formState.form.addEventListener('submit', handler)
	return true
}

async function handleFormSubmit(formState, event) {
	event.preventDefault()

	const validation = validateForm(formState)
	if (!validation.isValid) {
		setFormStatus(formState.form, 'error')
		setMessageState(formState, 'error', validation.message)
		return false
	}

	setSubmitting(formState, true)
	setSubmitImageSpinner(formState, true)
	setSubmitStartTime(formState)
	setFormStatus(formState.form, 'loading')
	setMessageState(formState, 'loading', '')
	let pendingStatus = null
	let pendingMessage = ''

	try {
		const response = await submitForm(formState)
		const result = await parseResponse(response)
		const didSucceed = Boolean(result.success)
		if (!didSucceed) {
			pendingStatus = 'error'
			pendingMessage = result.message || getMessage('genericError')
			setSubmitting(formState, false)
			return false
		}

		resetForm(formState.form)
		pendingStatus = 'success'
		pendingMessage = result.message || getMessage('success')
	} catch {
		pendingStatus = 'error'
		pendingMessage = getMessage('genericError')
	} finally {
		await ensureMinimumSubmitDuration(formState)
		setSubmitting(formState, false)
		setSubmitImageSpinner(formState, false)
		if (pendingStatus) {
			setFormStatus(formState.form, pendingStatus)
			setMessageState(formState, pendingStatus, pendingMessage)
		}
	}

	return true
}

function validateForm(formState) {
	const hasPendingSubmit = isSubmitting(formState)
	if (hasPendingSubmit) return { isValid: false, message: '' }

	const email = readEmailValue(formState.emailInput)
	const hasEmail = Boolean(email)
	if (!hasEmail) {
		return { isValid: false, message: getMessage('invalidEmail') }
	}

	const isValidEmail = isEmailValid(formState.emailInput, email)
	if (!isValidEmail) {
		return { isValid: false, message: getMessage('invalidEmail') }
	}

	const hasConsent = canSubmitWithConsent(formState.gdprCheckbox)
	if (!hasConsent) {
		return {
			isValid: false,
			message: getMessage('consentRequired'),
		}
	}

	return { isValid: true, message: '' }
}

function submitForm(formState) {
	const email = readEmailValue(formState.emailInput)
	return sendWaitingListRequest(email)
}

function sendWaitingListRequest(email) {
	const config = getConfig()
	const ajaxUrl = getAjaxUrl(config)
	const nonce = getNonce(config)
	const hasConfig = Boolean(ajaxUrl && nonce)
	if (!hasConfig) return Promise.resolve(new Response(null, { status: 500 }))

	const language = getCurrentLanguage(config.language)
	const params = buildRequestParams(email, nonce, language)
	return fetchRequest(ajaxUrl, params)
}

function buildRequestParams(email, nonce, language) {
	return new URLSearchParams({
		action: 'oblivio_waiting_list',
		nonce,
		email,
		lang: language,
	})
}

function fetchRequest(ajaxUrl, params) {
	return fetch(ajaxUrl, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
		},
		body: params.toString(),
		credentials: 'same-origin',
	})
}

async function parseResponse(response) {
	const isOk = response?.ok
	if (!isOk) return { success: false, message: '' }

	const payload = await response.json().catch(() => null)
	const success = Boolean(payload?.success)
	const message = payload?.data?.message || ''
	return { success, message }
}

function getCurrentLanguage(fallback) {
	if (fallback) return fallback

	const htmlLang = document.documentElement.lang || ''
	if (!htmlLang) return 'en'

	return htmlLang.split('-')[0].toLowerCase() || 'en'
}

function setFormStatus(form, status) {
	form.dataset.status = status
}

function setMessageState(formState, status, message) {
	const hasMessageWrapper = Boolean(formState.messageWrapper)
	if (!hasMessageWrapper) return false

	clearMessageStatus(formState.messageWrapper)
	applyMessageStatus(formState.messageWrapper, status)
	setMessageText(formState.messageText, message)
	const hasMessage = Boolean(message)
	if (!hasMessage) {
		resetMessageVisibility(formState)
		return true
	}

	showMessage(formState)
	return true
}

function clearMessageStatus(messageWrapper) {
	Object.values(messageStatusClasses).forEach((className) => {
		removeClass(messageWrapper, className)
	})
}

function applyMessageStatus(messageWrapper, status) {
	const className = messageStatusClasses[status]
	if (!className) return false

	addClass(messageWrapper, className)
	return true
}

function setMessageText(messageText, message) {
	if (!messageText) return false

	setText(messageText, message || '')
	return true
}

function showMessage(formState) {
	clearMessageTimers(formState)
	removeAttr(formState.messageWrapper, 'hiding')
	setAttr(formState.messageWrapper, 'show', '')
	scheduleHideMessage(formState)
}

function scheduleHideMessage(formState) {
	formState.messageTimers.hide = setTimeout(() => {
		setAttr(formState.messageWrapper, 'hiding', '')
		formState.messageTimers.clear = setTimeout(() => {
			resetMessageVisibility(formState)
		}, messageTiming.hideMs)
	}, messageTiming.visibleMs)
}

function resetMessageVisibility(formState) {
	clearMessageTimers(formState)
	removeAttr(formState.messageWrapper, 'show')
	removeAttr(formState.messageWrapper, 'hiding')
}

function clearMessageTimers(formState) {
	const hideTimer = formState.messageTimers.hide
	const clearTimer = formState.messageTimers.clear
	if (hideTimer) clearTimeout(hideTimer)
	if (clearTimer) clearTimeout(clearTimer)
	formState.messageTimers.hide = null
	formState.messageTimers.clear = null
}

function resetForm(form) {
	form.reset()
}

function readEmailValue(emailInput) {
	return emailInput?.value?.trim() || ''
}

function isEmailValid(emailInput, email) {
	const value = String(email).trim()
	if (!value) return false

	const canUseNative = Boolean(
		emailInput?.type === 'email' && emailInput?.checkValidity
	)
	if (canUseNative) {
		return emailInput.checkValidity()
	}

	return isEmailSafe(value)
}

function isEmailSafe(email) {
	const value = String(email).trim()
	if (!value) return false
	if (value.length > 254) return false
	if (/[<>"'()\\s]/.test(value)) return false
	return /^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$/.test(value)
}

function canSubmitWithConsent(gdprCheckbox) {
	const needsConsent = Boolean(gdprCheckbox)
	const hasConsent = !needsConsent || gdprCheckbox.checked
	return hasConsent
}

function getConfig() {
	return window?.oblivioConfig || {}
}

function getMessage(key) {
	const messages = getConfig().messages || {}
	const message = messages[key] || ''
	return message
}

function getAjaxUrl(config) {
	return config.ajaxUrl || '/wp-admin/admin-ajax.php'
}

function getNonce(config) {
	return config.nonce || ''
}

function isSubmitting(formState) {
	return Boolean(formState.isSubmitting)
}

function setSubmitting(formState, value) {
	formState.isSubmitting = value
}

function setSubmitImageSpinner(formState, shouldShow) {
	const image = formState.submitImage
	if (!image) return

	const originalSrc = formState.submitImageSrc
	if (!originalSrc) return

	if (shouldShow) {
		removeClass(image, spinnerConfig.className)
		if (formState.spinnerLoadHandler) {
			image.removeEventListener('load', formState.spinnerLoadHandler)
			formState.spinnerLoadHandler = null
		}
		const spinnerSrc = buildSpinnerSrc(originalSrc)
		if (image.getAttribute('src') === spinnerSrc) {
			addClass(image, spinnerConfig.className)
			return
		}

		const handleLoad = () => {
			if (image.getAttribute('src') === spinnerSrc) {
				addClass(image, spinnerConfig.className)
			}
			image.removeEventListener('load', handleLoad)
			formState.spinnerLoadHandler = null
		}

		formState.spinnerLoadHandler = handleLoad
		image.addEventListener('load', handleLoad)
		image.setAttribute('src', spinnerSrc)
		if (image.complete && image.getAttribute('src') === spinnerSrc) {
			addClass(image, spinnerConfig.className)
		}
		return
	}

	if (formState.spinnerLoadHandler) {
		image.removeEventListener('load', formState.spinnerLoadHandler)
		formState.spinnerLoadHandler = null
	}
	image.setAttribute('src', originalSrc)
	removeClass(image, spinnerConfig.className)
}

function buildSpinnerSrc(originalSrc) {
	const isAbsolute =
		originalSrc.startsWith('http://') || originalSrc.startsWith('https://')
	if (isAbsolute) {
		const url = new URL(originalSrc)
		const parts = url.pathname.split('/')
		parts[parts.length - 1] = spinnerConfig.filename
		url.pathname = parts.join('/')
		return url.toString()
	}

	const segments = originalSrc.split('/')
	segments[segments.length - 1] = spinnerConfig.filename
	return segments.join('/')
}

function setSubmitStartTime(formState) {
	formState.submitStartedAt = Date.now()
}

function ensureMinimumSubmitDuration(formState) {
	const minDurationMs = 2000
	const elapsedMs = Date.now() - formState.submitStartedAt
	const remainingMs = Math.max(minDurationMs - elapsedMs, 0)
	if (!remainingMs) return Promise.resolve()

	return new Promise((resolve) => {
		formState.submitTimers.min = setTimeout(resolve, remainingMs)
	})
}
